import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { EditorContent } from "@tiptap/react";
import { Popconfirm } from "antd";

import ImageWithBasePath from "../../../../core/common/imageWithBasePath";
import { BackendAllReport } from "../../reports/overview/types";
import { useTiptapEditor } from "../hooks/useEditor";
import { getFullName } from "../../reports-settings/report-type/helpers/form.handler";
import { exportReportToPDF } from "../helpers";
import { humanReadableDate } from "../../../../helper/dateTimeHelper";
import { commentService } from "../services/commentService";
import { notify } from "../../../../services/toast.config";

import "../styles/reportView.css";

const DisableScroll: React.FC = () => {
  useEffect(() => {
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, []);
  return null;
};

const CommentLoader: React.FC = () => (
  <div className="rvm-z7x9-spinner-container">
    <div className="rvm-z7x9-spinner"></div>
    <p className="rvm-z7x9-loader-text mb-0">Loading comments...</p>
  </div>
);

interface Comment {
  id: number;
  report_id: number;
  user: any;
  parent_id: number | null;
  depth: number;
  comment_text: string;
  is_edited: boolean;
  reply_count: number;
  created_at: string;
  updated_at: string;
  can_edit: boolean;
  can_delete: boolean;
  can_reply: boolean;
}

interface Reply extends Comment { }

const TiptapReadOnlyEditor: React.FC<{ content: string }> = ({ content }) => {
  const editor = useTiptapEditor({
    initialContent: content || "<p>No content available</p>",
    onChange: () => { },
    placeholder: "",
    editable: false,
  });

  useEffect(() => {
    if (editor) {
      editor.setEditable(false);
    }
  }, [editor]);

  return (
    <div className="tiptap-styled-content tiptap-readonly">
      <EditorContent editor={editor} />
    </div>
  );
};

interface ReportViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  report: BackendAllReport | null;
  showCreatedBy?: boolean;
}

const ReportViewModal: React.FC<ReportViewModalProps> = ({
  isOpen,
  onClose,
  report,
  showCreatedBy = false,
}) => {
  const MAX_NESTING_DEPTH = 3;

  const [comments, setComments] = useState<Comment[]>([]);
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    totalItems: 0,
    itemsPerPage: 20,
    hasNextPage: false,
    hasPrevPage: false,
  });

  const [newCommentText, setNewCommentText] = useState("");
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editText, setEditText] = useState("");

  const [replyToCommentId, setReplyToCommentId] = useState<number | null>(null);
  const [replyToRootId, setReplyToRootId] = useState<number | null>(null);
  const [replyText, setReplyText] = useState("");

  const [expandedCommentId, setExpandedCommentId] = useState<number | null>(null);
  const [replies, setReplies] = useState<Reply[]>([]);
  const [isLoadingReplies, setIsLoadingReplies] = useState(false);

  const [isCommentsCollapsed, setIsCommentsCollapsed] = useState(false);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen && report?.id) {
      fetchComments(1);
      setExpandedCommentId(null);
      setReplies([]);
    } else {
      setExpandedCommentId(null);
      setReplies([]);
      setEditingCommentId(null);
      setReplyToCommentId(null);
      setReplyToRootId(null);
      setNewCommentText("");
    }
  }, [isOpen, report?.id]);

  const fetchComments = async (page: number) => {
    if (!report?.id) return;

    setIsLoadingComments(true);
    try {
      const response = await commentService.getCommentsByReport({
        report_id: report.id,
        page,
        limit: 20,
      });

      const data = response.data?.data || {};
      setComments(data.comments || []);
      setPagination(data.pagination || {});
      setCurrentPage(page);
    } catch (error: any) {
      const errorData = error?.response?.data || error?.data;
      notify.error(errorData?.message?.[0]?.msg || "Failed to fetch comments");
    } finally {
      setIsLoadingComments(false);
    }
  };

  const loadReplies = async (commentId: number) => {
    setIsLoadingReplies(true);
    try {
      const response = await commentService.getRepliesByComment(commentId);
      const repliesData = response.data?.data?.replies || [];
      setReplies(repliesData);
    } catch (error) {
      notify.error("Failed to load replies");
      setReplies([]);
    } finally {
      setIsLoadingReplies(false);
    }
  };

  const toggleReplies = async (commentId: number) => {
    if (expandedCommentId === commentId) {
      setExpandedCommentId(null);
      setReplies([]);
    } else {
      setExpandedCommentId(commentId);
      await loadReplies(commentId);
    }
  };

  const handleCreateComment = async () => {
    if (!newCommentText.trim() || !report?.id) {
      notify.error("Please enter a comment");
      return;
    }

    setIsLoadingComments(true);
    try {
      await commentService.createComment({
        report_id: report.id,
        comment_text: newCommentText,
      });

      notify.success("Comment added successfully");
      setNewCommentText("");
      await fetchComments(currentPage);
    } catch (error: any) {
      const errorData = error?.response?.data || error?.data;
      notify.error(errorData?.message?.[0]?.msg || "Failed to add comment");
    } finally {
      setIsLoadingComments(false);
    }
  };

  const handleUpdateComment = async (commentId: number) => {
    if (!editText.trim()) {
      notify.error("Comment text cannot be empty");
      return;
    }

    setIsLoadingComments(true);
    try {
      await commentService.updateComment({
        comment_id: commentId,
        comment_text: editText,
      });

      notify.success("Comment updated successfully");
      setEditingCommentId(null);
      setEditText("");

      if (expandedCommentId !== null) {
        await loadReplies(expandedCommentId);
      }

      await fetchComments(currentPage);
    } catch (error: any) {
      const errorData = error?.response?.data || error?.data;
      notify.error(errorData?.message?.[0]?.msg || "Failed to update comment");
    } finally {
      setIsLoadingComments(false);
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    setIsLoadingComments(true);
    try {
      await commentService.deleteComment(commentId);
      notify.success("Comment deleted successfully");

      if (expandedCommentId === commentId) {
        setExpandedCommentId(null);
        setReplies([]);
      } else if (expandedCommentId !== null) {
        await loadReplies(expandedCommentId);
      }

      await fetchComments(currentPage);
    } catch (error: any) {
      const errorData = error?.response?.data || error?.data;
      notify.error(errorData?.message?.[0]?.msg || "Failed to delete comment");
    } finally {
      setIsLoadingComments(false);
    }
  };

  const handleReply = async (parentId: number) => {
    if (!replyText.trim() || !report?.id) {
      notify.error("Please enter a reply");
      return;
    }

    setIsLoadingComments(true);
    try {
      await commentService.createComment({
        report_id: report.id,
        comment_text: replyText,
        parent_id: parentId,
      });

      notify.success("Reply added successfully");
      setReplyToCommentId(null);
      setReplyText("");

      if (replyToRootId !== null && expandedCommentId === replyToRootId) {
        await loadReplies(replyToRootId);
      }

      setReplyToRootId(null);
      await fetchComments(currentPage);
    } catch (error: any) {
      const errorData = error?.response?.data || error?.data;
      notify.error(errorData?.message?.[0]?.msg || "Failed to add reply");
    } finally {
      setIsLoadingComments(false);
    }
  };

  const handleCancelReply = () => {
    setReplyToCommentId(null);
    setReplyToRootId(null);
    setReplyText("");
  };

  const buildReplyTree = (parentId: number, allReplies: Reply[], currentDepth: number): React.ReactNode[] => {
    const childReplies = allReplies.filter((r) => r.parent_id === parentId);

    return childReplies.map((reply) => (
      <div key={reply.id}>
        {renderReplyItem(reply, currentDepth)}
        {reply.can_reply && <div className="mt-2">{buildReplyTree(reply.id, allReplies, currentDepth + 1)}</div>}
      </div>
    ));
  };

  const renderReplyItem = (comment: Comment, currentDepth: number): React.ReactNode => {
    const depthClass = `rvm-z7x9-comment-depth-${Math.min(currentDepth + 1, 3)}`;

    return (
      <div className={depthClass}>
        <div className="rvm-z7x9-comment-reply">
          <div className="d-flex align-items-start">
            <div className="avatar avatar-sm avatar-rounded flex-shrink-0 me-2">
              <ImageWithBasePath
                src={
                  !comment.user?.profile_pic || comment.user?.profile_pic === "null"
                    ? "assets/img/users/user.png"
                    : comment.user?.profile_pic
                }
                alt="user"
              />
            </div>

            <div className="flex-grow-1" style={{ minWidth: 0 }}>
              <div className="d-flex align-items-center mb-1">
                <strong className="me-2 text-truncate" style={{ fontSize: "0.875rem", color: "#1e293b" }}>
                  {getFullName(comment.user?.salutation, comment.user?.fname, comment.user?.lname)}
                </strong>
                {comment.is_edited && (
                  <span
                    className="badge badge-sm"
                    style={{ fontSize: "0.625rem", backgroundColor: "#f1f5f9", color: "#64748b" }}>
                    Edited
                  </span>
                )}
              </div>
              <small className="text-muted d-block mb-2" style={{ fontSize: "0.75rem" }}>
                {humanReadableDate(comment.created_at, true)}
              </small>

              {editingCommentId === comment.id ? (
                <div className="mb-2">
                  <textarea
                    className="form-control rvm-z7x9-textarea mb-2"
                    rows={2}
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                  />
                  <button
                    className="btn btn-sm btn-success me-2 rvm-z7x9-btn-action"
                    onClick={() => handleUpdateComment(comment.id)}>
                    <i className="ti ti-check me-1"></i>Save
                  </button>
                  <button
                    className="btn btn-sm btn-secondary rvm-z7x9-btn-action"
                    onClick={() => {
                      setEditingCommentId(null);
                      setEditText("");
                    }}>
                    Cancel
                  </button>
                </div>
              ) : (
                <p
                  className="mb-2"
                  style={{ fontSize: "0.875rem", wordBreak: "break-word", color: "#334155", lineHeight: "1.5" }}>
                  {comment.comment_text}
                </p>
              )}

              <div className="rvm-z7x9-comment-actions">
                {comment.can_reply && (
                  <button
                    className="btn btn-link btn-sm p-0 text-primary rvm-z7x9-btn-action"
                    style={{ fontSize: "0.75rem", fontWeight: "500" }}
                    onClick={() => {
                      setReplyToCommentId(comment.id);
                      setReplyToRootId(expandedCommentId);
                      setReplyText("");
                    }}>
                    <i className="ti ti-message me-1"></i>Reply
                  </button>
                )}

                {comment.can_edit && (
                  <button
                    className="btn btn-link btn-sm p-0 text-warning rvm-z7x9-btn-action"
                    style={{ fontSize: "0.75rem", fontWeight: "500" }}
                    onClick={() => {
                      setEditingCommentId(comment.id);
                      setEditText(comment.comment_text);
                    }}>
                    <i className="ti ti-edit me-1"></i>Edit
                  </button>
                )}

                {comment.can_delete && (
                  <Popconfirm
                    title="Delete comment"
                    description="Are you sure?"
                    onConfirm={() => handleDeleteComment(comment.id)}
                    okText="Yes"
                    cancelText="No"
                    placement="topRight"
                    getPopupContainer={(trigger) => trigger.parentElement || document.body}>
                    <button
                      className="btn btn-link btn-sm p-0 text-danger rvm-z7x9-btn-action"
                      style={{ fontSize: "0.75rem", fontWeight: "500" }}>
                      <i className="ti ti-trash me-1"></i>Delete
                    </button>
                  </Popconfirm>
                )}
              </div>

              {replyToCommentId === comment.id && (
                <div className="mt-3">
                  <textarea
                    className="form-control rvm-z7x9-textarea mb-2"
                    rows={2}
                    placeholder="Write a reply..."
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                  />
                  <button
                    className="btn btn-sm btn-primary me-2 rvm-z7x9-btn-action"
                    onClick={() => handleReply(comment.id)}
                    disabled={!replyText.trim()}>
                    <i className="ti ti-send me-1"></i>Reply
                  </button>
                  <button className="btn btn-sm btn-secondary rvm-z7x9-btn-action" onClick={handleCancelReply}>
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderComment = (comment: Comment, currentDepth: number = 0): React.ReactNode => {
    const depthClass = `rvm-z7x9-comment-depth-${Math.min(currentDepth + 1, 3)}`;
    const isExpanded = expandedCommentId === comment.id;

    return (
      <div key={comment.id} className={depthClass}>
        <div className="rvm-z7x9-comment-item">
          <div className="d-flex align-items-start">
            <div className="avatar avatar-sm avatar-rounded flex-shrink-0 me-2">
              <ImageWithBasePath
                src={
                  !comment.user?.profile_pic || comment.user?.profile_pic === "null"
                    ? "assets/img/users/user.png"
                    : comment.user?.profile_pic
                }
                alt="user"
              />
            </div>

            <div className="flex-grow-1" style={{ minWidth: 0 }}>
              <div className="d-flex align-items-center mb-1">
                <strong className="me-2 text-truncate" style={{ fontSize: "0.875rem", color: "#1e293b" }}>
                  {getFullName(comment.user?.salutation, comment.user?.fname, comment.user?.lname)}
                </strong>
                {comment.is_edited && (
                  <span
                    className="badge badge-sm"
                    style={{ fontSize: "0.625rem", backgroundColor: "#f1f5f9", color: "#64748b" }}>
                    Edited
                  </span>
                )}
              </div>
              <small className="text-muted d-block mb-2" style={{ fontSize: "0.75rem" }}>
                {humanReadableDate(comment.created_at, true)}
              </small>

              {editingCommentId === comment.id ? (
                <div className="mb-2">
                  <textarea
                    className="form-control rvm-z7x9-textarea mb-2"
                    rows={2}
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                  />
                  <button
                    className="btn btn-sm btn-success me-2 rvm-z7x9-btn-action"
                    onClick={() => handleUpdateComment(comment.id)}>
                    <i className="ti ti-check me-1"></i>Save
                  </button>
                  <button
                    className="btn btn-sm btn-secondary rvm-z7x9-btn-action"
                    onClick={() => {
                      setEditingCommentId(null);
                      setEditText("");
                    }}>
                    Cancel
                  </button>
                </div>
              ) : (
                <p
                  className="mb-2"
                  style={{ fontSize: "0.875rem", wordBreak: "break-word", color: "#334155", lineHeight: "1.5" }}>
                  {comment.comment_text}
                </p>
              )}

              <div className="rvm-z7x9-comment-actions">



                {comment.can_reply && (
                  <button
                    className="btn btn-link btn-sm p-0 text-primary rvm-z7x9-btn-action"
                    style={{ fontSize: "0.75rem", fontWeight: "500" }}
                    onClick={() => {
                      setReplyToCommentId(comment.id);
                      setReplyToRootId(comment.id);
                      setReplyText("");
                    }}>
                    <i className="ti ti-message me-1"></i>Reply
                  </button>
                )}

                {comment.reply_count > 0 && (
                  <button
                    className="btn btn-link btn-sm p-0 text-info rvm-z7x9-btn-action"
                    style={{ fontSize: "0.75rem", fontWeight: "500" }}
                    onClick={() => toggleReplies(comment.id)}
                    disabled={isLoadingReplies && expandedCommentId === comment.id}>
                    {isLoadingReplies && expandedCommentId === comment.id ? (
                      <span className="spinner-border spinner-border-sm me-1"></span>
                    ) : (
                      <i className="ti ti-messages me-1"></i>
                    )}
                    {isExpanded ? "Hide" : "Show"} {comment.reply_count}
                  </button>
                )}

                {comment.can_edit && (
                  <button
                    className="btn btn-link btn-sm p-0 text-warning rvm-z7x9-btn-action"
                    style={{ fontSize: "0.75rem", fontWeight: "500" }}
                    onClick={() => {
                      setEditingCommentId(comment.id);
                      setEditText(comment.comment_text);
                    }}>
                    <i className="ti ti-edit me-1"></i>Edit
                  </button>
                )}

                {comment.can_delete && (
                  <Popconfirm
                    title="Delete comment"
                    description="Are you sure?"
                    onConfirm={() => handleDeleteComment(comment.id)}
                    okText="Yes"
                    cancelText="No"
                    placement="topRight"
                    getPopupContainer={(trigger) => trigger.parentElement || document.body}>
                    <button
                      className="btn btn-link btn-sm p-0 text-danger rvm-z7x9-btn-action"
                      style={{ fontSize: "0.75rem", fontWeight: "500" }}>
                      <i className="ti ti-trash me-1"></i>Delete
                    </button>
                  </Popconfirm>
                )}
              </div>

              {replyToCommentId === comment.id && (
                <div className="mt-3">
                  <textarea
                    className="form-control rvm-z7x9-textarea mb-2"
                    rows={2}
                    placeholder="Write a reply..."
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                  />
                  <button
                    className="btn btn-sm btn-primary me-2 rvm-z7x9-btn-action"
                    onClick={() => handleReply(comment.id)}
                    disabled={!replyText.trim()}>
                    <i className="ti ti-send me-1"></i>Reply
                  </button>
                  <button className="btn btn-sm btn-secondary rvm-z7x9-btn-action" onClick={handleCancelReply}>
                    Cancel
                  </button>
                </div>
              )}

              {isExpanded && replies.length > 0 && (
                <div className="mt-3">{buildReplyTree(comment.id, replies, currentDepth + 1)}</div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (!isOpen || !report) return null;

  const handleContentClick = (e: React.MouseEvent) => e.stopPropagation();

  const getFrequencyDisplayText = (): string => {
    const { frequency_type, weekly_interval, monthly_interval } = report;
    if (!frequency_type) return "N/F";

    switch (frequency_type) {
      case "daily":
        return "Daily";
      case "weekly":
        return weekly_interval === 1 ? "Weekly" : `Every ${weekly_interval} weeks`;
      case "monthly":
        return monthly_interval === 1 ? "Monthly" : `Every ${monthly_interval} months`;
      default:
        return frequency_type.charAt(0).toUpperCase() + frequency_type.slice(1);
    }
  };

  const createdDate = humanReadableDate(report.created_at, true);
  const reportDate = humanReadableDate(report.report_date);
  const submittedDate = report.submitted_at ? humanReadableDate(report.submitted_at, true) : null;
  const isDraft = report.status_text === "Draft";

  return createPortal(
    <>
      <DisableScroll />

      <div className="rvm-z7x9-backdrop" role="dialog" aria-modal="true" aria-labelledby="report-modal-title">
        <div className="rvm-z7x9-container" onClick={handleContentClick}>
          <div className="rvm-z7x9-header">
            <div className="rvm-z7x9-header-content">
              <h3
                id="report-modal-title"
                style={{ margin: 0, fontSize: "1.375rem", fontWeight: "600", color: "#0f172a", lineHeight: "1.3" }}>
                {report.report_title}
              </h3>

              <div className="mt-2 d-flex flex-wrap gap-2 align-items-center">
                {report.status_text && (
                  <span className="badge badge-sm px-2 py-1" style={{ backgroundColor: "#f1f5f9", color: "#475569" }}>
                    {report.status_text}
                  </span>
                )}

                <small className="badge badge-sm badge-secondary-transparent px-2 py-1">
                  <i className="ti ti-repeat me-1"></i>
                  {getFrequencyDisplayText()}
                </small>

                {!isDraft && (
                  <span className={`badge badge-sm badge-${report.is_submitted_late ? "danger" : "success"} px-2 py-1`}>
                    {report.is_submitted_late ? "Late" : "On Time"}
                  </span>
                )}

                <small style={{ fontSize: "0.813rem", color: "#64748b" }}>
                  <i className="ti ti-calendar me-1"></i>
                  {reportDate}
                </small>

                <small style={{ fontSize: "0.813rem", color: "#64748b" }}>
                  <i className="ti ti-clock me-1"></i>
                  {isDraft ? createdDate : submittedDate || "No Date"}
                </small>

                {showCreatedBy && (
                  <div className="d-flex align-items-center">
                    <div className="avatar avatar-sm avatar-rounded flex-shrink-0 me-1">
                      <ImageWithBasePath
                        src={
                          !report?.created_by?.profile_pic || report?.created_by?.profile_pic === "null"
                            ? "assets/img/users/user.png"
                            : report?.created_by?.profile_pic
                        }
                        alt="user"
                      />
                    </div>
                    <small style={{ fontSize: "0.813rem", color: "#64748b" }}>
                      {getFullName(report?.created_by?.salutation, report?.created_by?.fname, report?.created_by?.lname)}
                    </small>
                  </div>
                )}
              </div>
            </div>

            <div className="rvm-z7x9-header-actions bg-soft-danger p-2 rounded-3">
              <button type="button" className="btn-close" onClick={onClose} aria-label="Close" title="Close Modal" />
            </div>
          </div>

          <div className="rvm-z7x9-body">
            <div className="rvm-z7x9-content-side">
              <div className="rvm-z7x9-content-wrapper">
                {report.report_content ? (
                  <TiptapReadOnlyEditor content={report.report_content} />
                ) : (
                  <div className="text-center py-5">
                    <i className="ti ti-file-text text-muted" style={{ fontSize: "3rem" }}></i>
                    <h5 className="mt-3 mb-2 text-muted">No Content Available</h5>
                    <p className="text-muted">This report doesn't contain any content yet.</p>
                  </div>
                )}
              </div>

              <div className="rvm-z7x9-content-footer">
                <button
                  type="button"
                  className="btn btn-success btn-sm rvm-z7x9-btn-action"
                  onClick={() => exportReportToPDF(report)}>
                  <i className="ti ti-file-export me-1"></i>Export
                </button>
                <button type="button" className="btn btn-danger btn-sm rvm-z7x9-btn-action" onClick={onClose}>
                  <i className="ti ti-x me-1"></i>Close
                </button>
              </div>
            </div>

            <div className={`rvm-z7x9-comments-side ${isCommentsCollapsed ? "collapsed" : ""}`}>
              <div className="rvm-z7x9-comments-header">
                <h6 className="rvm-z7x9-comments-header-title">
                  <i className="ti ti-messages me-2"></i>
                  Comments ({pagination.totalItems || 0})
                </h6>
                <button
                  className="rvm-z7x9-collapse-btn"
                  onClick={() => setIsCommentsCollapsed(!isCommentsCollapsed)}
                  title={isCommentsCollapsed ? "Expand Comments" : "Collapse Comments"}>
                  <i className={`ti ti-chevron-${isCommentsCollapsed ? "left" : "right"}`}></i>
                </button>
              </div>

              {!isCommentsCollapsed && (
                <>
                  <div className="rvm-z7x9-comments-list">
                    {isLoadingComments && (
                      <div className="rvm-z7x9-loader-overlay">
                        <CommentLoader />
                      </div>
                    )}

                    {!isLoadingComments && comments.length > 0 ? (
                      <>
                        {comments.map((comment) => renderComment(comment, 0))}
                        {pagination.totalPages > 1 && (
                          <div className="d-flex justify-content-center gap-2 mt-3">
                            <button
                              className="btn btn-sm btn-outline-secondary rvm-z7x9-btn-action"
                              disabled={!pagination.hasPrevPage}
                              onClick={() => fetchComments(currentPage - 1)}>
                              <i className="ti ti-chevron-left"></i>
                            </button>
                            <span className="btn btn-sm btn-secondary disabled" style={{ fontSize: "0.75rem" }}>
                              {currentPage} / {pagination.totalPages}
                            </span>
                            <button
                              className="btn btn-sm btn-outline-secondary rvm-z7x9-btn-action"
                              disabled={!pagination.hasNextPage}
                              onClick={() => fetchComments(currentPage + 1)}>
                              <i className="ti ti-chevron-right"></i>
                            </button>
                          </div>
                        )}
                      </>
                    ) : (
                      !isLoadingComments && (
                        <div className="rvm-z7x9-empty-state">
                          <svg className="rvm-z7x9-empty-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1.5}
                              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                            />
                          </svg>
                          <p style={{ fontSize: "0.938rem", color: "#64748b", fontWeight: "500" }}>
                            No comments yet. Be the first!
                          </p>
                        </div>
                      )
                    )}
                  </div>

                  <div className="rvm-z7x9-comments-input">
                    <textarea
                      className="form-control rvm-z7x9-textarea mb-2"
                      rows={3}
                      placeholder="Write a comment..."
                      value={newCommentText}
                      onChange={(e) => setNewCommentText(e.target.value)}
                    />
                    <button
                      className="btn btn-primary btn-sm w-100 rvm-z7x9-btn-action"
                      onClick={handleCreateComment}
                      disabled={!newCommentText.trim() || isLoadingComments}>
                      <i className="ti ti-send me-1"></i>Post Comment
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default ReportViewModal;
